/**
 * Operation types — OpResult, input interfaces.
 *
 * All operations return OpResult<T>. Never throw (except unexpected DB errors).
 */

import type { ObjectType, StartAtType, StartAtSubType } from "./db-types.js"

// ---------------------------------------------------------------------------
// OpResult — universal return type for all operations
// ---------------------------------------------------------------------------

export interface OpSuccess<T = void> {
  ok: true
  data: T
}

/**
 * Why an operation failed, for a caller that has to tell failures apart (REST answers
 * "not_found" with 404, every other failure with 400). Most failures carry none.
 */
export type OpErrorCode = "not_found"

export interface OpFailure {
  ok: false
  error: string
  code?: OpErrorCode
}

export type OpResult<T = void> = OpSuccess<T> | OpFailure

export function ok(): OpResult
export function ok<T>(data: T): OpResult<T>
export function ok<T>(data?: T): OpResult<T> {
  return { ok: true, data: data as T }
}

export function fail(error: string, code?: OpErrorCode): OpFailure {
  return code ? { ok: false, error, code } : { ok: false, error }
}

// ---------------------------------------------------------------------------
// Insert position
// ---------------------------------------------------------------------------

export type InsertPosition =
  | "top"
  | "bottom"
  | "beforeFirstArea"
  | { afterId: string }
  | { beforeId: string }

// ---------------------------------------------------------------------------
// AddTask options
// ---------------------------------------------------------------------------

export interface AddTaskOpts {
  title: string
  parentPath?: string
  type?: ObjectType
  insertPosition?: InsertPosition
  startAt?: string
  startAtType?: StartAtType
  startAtSubType?: StartAtSubType
  tags?: string[]
  dueAt?: string
  note?: string
  /** Rich text content. Converted to Y.Doc if docContext is configured. */
  docContent?: string
  /** Format of docContent. Auto-detected if omitted. */
  docFormat?: "text" | "html" | "json" | "markdown"
  realmId?: string
  owner?: string
  /**
   * Use this id for the new object instead of a random one. Callers that must find the object
   * again after a retry (the MCP idempotency ledger) derive it from the request. Adding an id
   * that exists fails, like any duplicate add.
   */
  id?: string
  /**
   * Extra columns written with the row itself (a plan step's anchorTaskId / planId), so there is
   * no moment where the object exists without them. Never the row's identity: id, owner and realmId
   * have their own options (and are what the op resolves).
   */
  fields?: Omit<Partial<import("./db-types.js").DbObject>, "id" | "owner" | "realmId">
}

// ---------------------------------------------------------------------------
// Move options
// ---------------------------------------------------------------------------

export interface MoveOpts {
  insertPosition?: InsertPosition
}

// ---------------------------------------------------------------------------
// Reorder options
// ---------------------------------------------------------------------------

export interface ReorderOpts {
  primaryField: "index" | "startAtIndex" | "privateIndex"
  afterId?: string
  beforeId?: string
  newParentPath?: string
}

// ---------------------------------------------------------------------------
// Schedule options
// ---------------------------------------------------------------------------

export interface ScheduleOpts {
  startAt: string
  type?: StartAtType
  subType?: StartAtSubType
  insertPosition?: InsertPosition
}

// ---------------------------------------------------------------------------
// Container creation options
// ---------------------------------------------------------------------------

export interface ContainerOpts {
  parentPath?: string
  insertPosition?: InsertPosition
  realmId?: string
  owner?: string
  /** Use this id for the new container instead of a random one (see AddTaskOpts.id). */
  id?: string
}
